// Button components with multiple styles.
import { ChevronDown, ChevronUp, Close, Information } from '@carbon/icons-react';
import React, { useState } from 'react';
import type { DimensionValue } from 'react-native';
import { ActivityIndicator, Image, StyleSheet, View } from 'react-native';
import type { FormatParams, TLabel } from 'util/stdtypes';
import { makeTestID, t } from 'util/stdtypes';
import { toHoverColor, toPressColor, translucentHoverColor, translucentPressColor } from 'util/util-color';

import { HoverView, Pad, PadBox } from './basics';
import {
    colorBorderFacepile,
    colorBorderPrimary,
    colorBorderSelected,
    colorButtonAccent,
    colorButtonDisabled,
    colorButtonPrimary,
    colorButtonSecondary,
    colorSurfaceBannerInfo,
    colorSurfacePrimary,
    colorTextAccent,
    colorTextDisabled,
    colorTextOnAccentColorBg,
    colorTextPrimary,
    colorTextSecondary,
    colorTextSecondaryOnBanner,
    colorTextWarning,
} from './color';
import type { TextType, TextWeight } from './text';
import { DEPRECATED_EditorialHeading, Heading, Paragraph, UtilityText } from './text';
import { useTranslation } from './translation';
export type ButtonType = 'primary' | 'secondary' | 'accent' | 'disabled' | 'delete';
export type ButtonSize = 'large' | 'compact' | 'small';

export type CTAButtonProps = {
    label?: TLabel;
    text?: string;
    testID?: string;
    formatParams?: FormatParams;
    leftIcon?: React.ReactNode;
    icon?: React.ReactNode;
    type?: ButtonType;
    disabled?: boolean;
    size?: ButtonSize;
    wide?: boolean;
    borderless?: boolean;
    inProgress?: boolean;
    onPress?: () => void;
};

export function CTAButton({
    label,
    text,
    testID,
    formatParams,
    leftIcon,
    icon,
    type = 'primary',
    disabled = false,
    size = 'large',
    wide = false,
    borderless = false,
    inProgress = false,
    onPress,
}: CTAButtonProps) {
    const s = CTAButtonStyle;

    const styleMap = {
        primary: { normal: s.primary, hover: s.primaryHover, pressed: s.primaryPressed, textColor: colorTextOnAccentColorBg },
        secondary: { normal: s.secondary, hover: s.secondaryHover, pressed: s.secondaryPressed, textColor: colorTextPrimary },
        accent: { normal: s.accent, hover: s.accentHover, pressed: s.accentPressed, textColor: colorTextOnAccentColorBg },
        disabled: { normal: s.disabled, hover: s.disabled, pressed: s.disabled, textColor: colorTextDisabled },
        delete: { normal: s.delete, hover: s.deleteHover, pressed: s.deletePressed, textColor: colorTextWarning },
    };
    const { normal, hover, pressed, textColor } = styleMap[disabled ? 'disabled' : type] ?? styleMap.primary;

    const sizeMap = {
        large: s.largeButton,
        compact: s.compactButton,
        small: s.smallButton,
    };

    const progressColor = type === 'secondary' || type === 'delete' ? colorButtonAccent : colorTextOnAccentColorBg;

    return (
        <HoverView
            disabled={disabled}
            hoverStyle={[s.hover, hover]}
            onPress={onPress}
            pressedStyle={pressed}
            role='button'
            style={[sizeMap[size], s.base, wide && s.wide, normal, borderless && s.borderless]}
            testID={makeTestID({ testID, label, text })}
        >
            {leftIcon && <PadBox right={label || text ? (size === 'small' ? 6 : 8) : undefined}>{leftIcon}</PadBox>}
            {!inProgress && (
                <UtilityText
                    color={textColor}
                    formatParams={formatParams}
                    label={label}
                    numberOfLines={1}
                    text={text}
                    type={size === 'small' ? 'tiny' : 'large'}
                />
            )}
            {inProgress && <ActivityIndicator color={progressColor} size={'small'} style={s.activityIndicator} />}
            {icon && <PadBox left={label || text ? (size === 'small' ? 6 : 8) : undefined}>{icon}</PadBox>}
        </HoverView>
    );
}

const CTAButtonStyle = StyleSheet.create({
    activityIndicator: {},
    base: {
        maxWidth: '100%',
    },
    hover: {},
    primary: {
        backgroundColor: colorButtonPrimary,
        borderColor: colorButtonPrimary,
    },
    primaryHover: {
        backgroundColor: toHoverColor(colorButtonPrimary),
        borderColor: toHoverColor(colorButtonPrimary),
    },
    primaryPressed: {
        backgroundColor: toPressColor(colorButtonPrimary),
        borderColor: toPressColor(colorButtonPrimary),
    },
    secondary: {
        borderColor: colorBorderPrimary,
        backgroundColor: colorButtonSecondary,
    },
    secondaryPressed: {
        backgroundColor: toPressColor(colorButtonSecondary),
    },
    secondaryHover: {
        backgroundColor: toHoverColor(colorButtonSecondary),
    },
    accent: {
        backgroundColor: colorButtonAccent,
        borderColor: colorButtonAccent,
    },
    accentPressed: {
        backgroundColor: toPressColor(colorButtonAccent),
        borderColor: toPressColor(colorButtonAccent),
    },
    accentHover: {
        backgroundColor: toHoverColor(colorButtonAccent),
        borderColor: toHoverColor(colorButtonAccent),
    },
    delete: {
        borderColor: colorBorderPrimary,
    },
    deletePressed: {
        backgroundColor: toPressColor(colorSurfacePrimary),
        borderColor: colorBorderPrimary,
    },
    deleteHover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
        borderColor: colorBorderPrimary,
    },
    disabled: {
        backgroundColor: colorButtonDisabled,
        borderColor: colorButtonDisabled,
        color: colorTextDisabled,
    },
    largeButton: {
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 32,
        alignSelf: 'flex-start',
        borderWidth: 1,
        flexDirection: 'row',
        height: 44,
        alignItems: 'center',
    },
    compactButton: {
        paddingHorizontal: 12,
        height: 40,
        borderRadius: 32,
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
    },
    smallButton: {
        paddingHorizontal: 12,
        height: 32,
        borderRadius: 32,
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
    },
    wide: {
        alignSelf: 'stretch',
        justifyContent: 'center',
        flex: 1,
    },
    borderless: {
        borderWidth: 0,
    },
});

export type ClickThroughTagProps = { label?: TLabel; text?: string; emoji?: string; onPress?: () => void };

export function ClickThroughTag({ label, text, emoji, onPress }: ClickThroughTagProps) {
    return (
        <CTAButton
            borderless
            label={label}
            leftIcon={emoji ? <UtilityText text={emoji} type='small' weight='medium' /> : undefined}
            onPress={onPress}
            size='small'
            text={text}
            type='secondary'
        />
    );
}

export type IconButtonProps = {
    icon?: React.ComponentType<any>;
    onPress?: () => void;
    iconProps?: Record<string, unknown>;
    testID?: string;
};

export function IconButton({ icon, onPress, iconProps = {}, testID }: IconButtonProps) {
    const s = IconButtonStyle;
    return (
        <HoverView hoverStyle={s.hover} onPress={onPress} pressedStyle={s.pressed} role='button' style={s.button} testID={testID}>
            {icon && React.createElement(icon, iconProps)}
        </HoverView>
    );
}
const IconButtonStyle = StyleSheet.create({
    button: {
        color: colorTextPrimary,
        height: 32,
        width: 32,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        backgroundColor: colorSurfacePrimary,
    },
    hover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(colorSurfacePrimary),
    },
});

export type ActionButtonProps = {
    label?: TLabel;
    text?: string;
    testID?: string;
    ariaLabel?: TLabel;
    icon?: React.ComponentType<any>;
    iconProps?: Record<string, unknown>;
    wide?: boolean;
    onPress?: () => void;
    textColor?: string;
    hover?: boolean;
    pressed?: boolean;
    disabled?: boolean;
};

export function ActionButton({
    label,
    text,
    testID,
    ariaLabel,
    icon,
    iconProps = {},
    wide = false,
    onPress,
    textColor,
    hover = false,
    pressed = false,
    disabled = false,
}: ActionButtonProps) {
    const s = ActionButtonStyle;
    const iconColorOverride = 'color' in iconProps ? iconProps.color : undefined;
    const iconColor = disabled ? colorTextDisabled : iconColorOverride;
    return (
        <HoverView
            ariaLabel={ariaLabel}
            disabled={disabled}
            hoverStyle={[s.hover]}
            onPress={onPress}
            pressedStyle={s.pressed}
            role='button'
            style={[s.button, wide ? s.wide : null, hover ? s.hover : null]}
            testID={makeTestID({ testID, ariaLabel, label, text })}
        >
            {icon && React.createElement(icon, { ...iconProps, color: iconColor })}
            {icon && (label || text) && <Pad size={8} />}
            <UtilityText color={disabled ? colorTextDisabled : textColor} label={label} text={text} />
        </HoverView>
    );
}
const ActionButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        height: 32,
        alignItems: 'center',
        paddingHorizontal: 8,
        alignSelf: 'flex-start',
        borderRadius: 32,
        color: colorTextPrimary,
        backgroundColor: colorSurfacePrimary,
    },
    wide: {
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    hover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(colorSurfacePrimary),
    },
    disabled: {
        color: colorTextDisabled,
    },
});

export type SubtleButtonProps = {
    label?: TLabel;
    text?: string;
    ariaLabel?: TLabel;
    testID?: string;
    disabled?: boolean;
    formatParams?: FormatParams;
    color?: string;
    icon?: React.ComponentType<any>;
    iconProps?: Record<string, unknown>;
    padLeft?: boolean;
    padRight?: boolean;
    onPress?: () => void;
};

export function SubtleButton({
    label,
    text,
    ariaLabel,
    testID,
    disabled = false,
    formatParams,
    color = colorTextSecondary,
    icon,
    iconProps,
    padLeft = false,
    padRight = false,
    onPress,
}: SubtleButtonProps) {
    const s = SubtleButtonStyle;
    const [hover, setHover] = useState(false);
    return (
        <HoverView
            ariaLabel={ariaLabel}
            disabled={disabled}
            onPress={onPress}
            role={!disabled ? 'button' : undefined}
            setHover={setHover}
            style={[s.button, padLeft && { marginLeft: 20 }, padRight && { marginRight: 20 }]}
            testID={makeTestID({ testID, ariaLabel, label, text })}
        >
            {icon && React.createElement(icon, { ...iconProps, color: disabled ? colorButtonDisabled : color })}
            {(label || text) && <Pad size={4} />}
            <UtilityText
                color={disabled ? colorButtonDisabled : color}
                formatParams={formatParams}
                label={label}
                text={text}
                type='small'
                underline={hover}
                weight={'medium'}
            />
        </HoverView>
    );
}
const SubtleButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export type TextButtonProps = {
    label?: TLabel;
    text?: string;
    weight?: TextWeight;
    type?: TextType;
    heading?: boolean;
    paragraph?: boolean;
    editorial?: boolean;
    underline?: boolean;
    strong?: boolean;
    italic?: boolean;
    formatParams?: FormatParams;
    leftIcon?: React.ComponentType<any>;
    leftIconProps?: Record<string, unknown>;
    rightIcon?: React.ComponentType<any>;
    rightIconProps?: Record<string, unknown>;
    color?: string;
    alignStart?: boolean;
    onPress?: () => void;
    tabIndex?: string;
    ariaHidden?: boolean;
};

export function TextButton({
    label,
    text,
    weight = 'regular',
    type = 'large',
    heading = false,
    paragraph = false,
    editorial = false,
    underline = false,
    strong = false,
    italic = false,
    formatParams,
    leftIcon,
    leftIconProps = {},
    rightIcon,
    rightIconProps = {},
    color = colorTextPrimary,
    alignStart = false,
    onPress,
    tabIndex,
    ariaHidden = false,
}: TextButtonProps) {
    const s = TextButtonStyle;
    const [hover, setHover] = useState(false);
    return (
        <HoverView
            aria-hidden={ariaHidden}
            onPress={onPress}
            role='button'
            setHover={setHover}
            shrink
            style={[s.button, alignStart ? { alignSelf: 'flex-start' } : null]}
            tabIndex={tabIndex}
            testID={makeTestID({ label, text })}
        >
            {leftIcon && React.createElement(leftIcon, { ...leftIconProps, color })}
            {leftIcon && <Pad size={8} />}
            {paragraph ? (
                <Paragraph
                    color={color}
                    formatParams={formatParams}
                    label={label}
                    strong={strong}
                    text={text}
                    type={type}
                    underline={hover != underline}
                />
            ) : editorial ? (
                <DEPRECATED_EditorialHeading
                    color={color}
                    formatParams={formatParams}
                    italic={italic}
                    label={label}
                    text={text}
                    type={type}
                    underline={hover != underline}
                />
            ) : heading ? (
                <Heading
                    color={color}
                    formatParams={formatParams}
                    label={label}
                    text={text}
                    type={type}
                    underline={hover != underline}
                    weight={strong ? 'medium' : 'regular'}
                />
            ) : (
                <UtilityText
                    color={color}
                    formatParams={formatParams}
                    label={label}
                    text={text}
                    type={type}
                    underline={hover != underline}
                    weight={strong ? 'medium' : 'regular'}
                />
            )}
            {rightIcon && <Pad size={8} />}
            {rightIcon && React.createElement(rightIcon, { ...rightIconProps, color })}
        </HoverView>
    );
}
const TextButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export type TextLinkButtonProps = {
    label: TLabel;
    text: string;
    type?: TextType;
    paragraph?: boolean;
    editorial?: boolean;
    underline?: boolean;
    strong?: boolean;
    italic?: boolean;
    formatParams?: FormatParams;
    leftIcon?: any;
    rightIcon?: any;
    color?: string;
    alignStart?: boolean;
    onPress?: () => void;
};

export function TextLinkButton({
    label,
    text,
    type = 'large' as TextType,
    paragraph = false,
    editorial = false,
    underline = false,
    strong = false,
    italic = false,
    formatParams,
    leftIcon,
    rightIcon,
    color = colorTextPrimary,
    alignStart = false,
    onPress,
}: TextLinkButtonProps) {
    const [hover, setHover] = useState(false);
    return (
        <HoverView
            onPress={onPress}
            role='button'
            setHover={setHover}
            shrink
            style={[alignStart ? { alignSelf: 'flex-start' } : null]}
            testID={makeTestID({ label, text })}
        >
            {leftIcon && React.createElement(leftIcon, { color })}
            {leftIcon && <Pad size={8} />}
            {paragraph ? (
                <Paragraph
                    color={color}
                    formatParams={formatParams}
                    label={label}
                    strong={strong}
                    text={text}
                    type={type}
                    underline={hover != underline}
                />
            ) : editorial ? (
                <DEPRECATED_EditorialHeading
                    color={color}
                    formatParams={formatParams}
                    italic={italic}
                    label={label}
                    text={text}
                    type={type}
                    underline={hover != underline}
                />
            ) : (
                <UtilityText
                    color={color}
                    formatParams={formatParams}
                    label={label}
                    strong={strong}
                    text={text}
                    type={type}
                    underline={hover != underline}
                />
            )}
            {rightIcon && <Pad size={8} />}
            {rightIcon && React.createElement(rightIcon, { color })}
        </HoverView>
    );
}

export type ExpandButtonProps = {
    summaryElement?: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
    label?: TLabel;
    text?: string;
    type?: TextType;
    formatParams?: FormatParams;
    expanded?: boolean;
    setExpanded?: (e: boolean) => void;
    testID?: string;
};

export function ExpandButton({
    summaryElement,
    label,
    text,
    type = 'tiny' as TextType,
    formatParams,
    expanded = false,
    setExpanded = (e: boolean) => {},
    testID,
}: ExpandButtonProps) {
    const s = ExpanderButtonStyle;
    const [hover, setHover] = useState(false);

    const tLabel = useTranslation(label, formatParams);
    const ariaText = useTranslation(expanded ? '{text} - hide' : '{text} - show', { text: tLabel ?? text });

    return (
        <HoverView
            ariaText={ariaText}
            onPress={() => setExpanded(!expanded)}
            role='button'
            setHover={setHover}
            style={s.button}
            testID={makeTestID({ testID, label, text })}
        >
            {summaryElement && <PadBox right={4}>{summaryElement}</PadBox>}
            <UtilityText aria-hidden formatParams={formatParams} label={label} text={text} type={type} underline={hover} weight='medium' />
            <Pad size={4} />
            {expanded ? <ChevronUp color={colorTextPrimary} size={20} /> : <ChevronDown color={colorTextPrimary} size={20} />}
        </HoverView>
    );
}
const ExpanderButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});

export type TagProps = {
    label?: TLabel;
    emoji?: string;
    text?: string;
    type?: string;
    strong?: boolean;
    formatParams?: FormatParams;
    color?: string;
    compact?: boolean;
    dashed?: boolean;
    borderColor?: string;
    overrideTinyTextColor?: boolean;
    textColor?: string;
    maxWidth?: string;
};

export function Tag({
    label,
    emoji,
    text,
    type = 'emphasized',
    strong = false,
    formatParams,
    color,
    compact = false,
    dashed = false,
    borderColor,
    overrideTinyTextColor = false,
    textColor = colorTextSecondaryOnBanner,
    maxWidth = '100%',
}: TagProps) {
    const s = TagStyle;
    return (
        <View
            style={[
                s.button,
                type == 'emphasized' && s.emphasized,
                type == 'tiny' && s.tiny,
                color ? { borderColor: borderColor || color, backgroundColor: color } : null,
                compact && { height: 28 },
                dashed && { borderStyle: 'dashed' },
                { maxWidth: maxWidth as DimensionValue },
            ]}
        >
            {emoji && (
                <PadBox right={6}>
                    <UtilityText text={emoji} type='tiny' weight='medium' />
                </PadBox>
            )}
            <UtilityText
                color={type == 'tiny' && !overrideTinyTextColor ? colorTextAccent : textColor}
                formatParams={formatParams}
                label={label}
                numberOfLines={1}
                text={text}
                type='tiny'
                weight={strong ? 'strong' : 'medium'}
            />
        </View>
    );
}
const TagStyle = StyleSheet.create({
    button: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderWidth: 1,
        borderRadius: 4,
        height: 32,
        // borderColor: 'red',
        borderColor: colorBorderPrimary,
    },
    emphasized: {
        borderRadius: 100,
    },
    tiny: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 100,
        height: 20,
        backgroundColor: colorSurfaceBannerInfo,
        borderColor: colorSurfaceBannerInfo,
    },
});

export type BetaTagProps = { type?: string };

export function BetaTag({ type = 'primary' }: BetaTagProps) {
    return (
        <View style={[BetaTagStyle[type], BetaTagStyle.container]}>
            <PadBox horiz={8} vert={2.5}>
                <UtilityText caps color={colorTextOnAccentColorBg} label={t('Beta')} type='tiny' weight='medium' />
            </PadBox>
        </View>
    );
}

const BetaTagStyle = StyleSheet.create({
    container: {
        borderRadius: 32,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        alignSelf: 'flex-start',
    },
    primary: {
        backgroundColor: colorButtonAccent,
    },
    secondary: {
        backgroundColor: 'transparent',
        borderColor: colorBorderFacepile,
        borderWidth: 1,
    },
});

export type ReactionButtonProps = {
    emoji?: string;
    icon?: React.ComponentType<any>;
    wide?: boolean;
    iconProps?: { color: string };
    viewOnly?: boolean;
    label?: TLabel;
    text?: string;
    count?: number;
    selected?: boolean;
    onPress?: () => void;
};

export function ReactionButton({
    emoji,
    icon,
    wide = false,
    iconProps = { color: colorTextPrimary },
    viewOnly = false,
    label,
    text,
    count,
    selected = false,
    onPress,
}: ReactionButtonProps) {
    const s = ReactionButtonStyle;
    const tSelected = useTranslation(selected ? 'selected' : 'deselected');
    const tLabel = useTranslation(label) ?? text;
    const ariaText = useTranslation('{count} {tLabel} ({tSelected})', { count: count ?? '', tLabel, tSelected });
    return (
        <HoverView
            ariaText={ariaText}
            disabled={viewOnly}
            hoverStyle={s.hover}
            onPress={onPress}
            pressedStyle={s.pressed}
            role='button'
            style={[s.horiz, !viewOnly && s.button, selected && s.pressed, wide && s.wide]}
            testID={makeTestID({ label, text })}
        >
            {icon && React.createElement(icon, iconProps)}
            {emoji && <UtilityText aria-hidden text={emoji} type='small' weight='medium' />}
            {label || text ? <UtilityText label={label} text={text} type='small' weight='medium' /> : null}
            {count ? <UtilityText color={colorTextWarning} text={count.toString()} type='small' weight='medium' /> : null}
        </HoverView>
    );
}
const ReactionButtonStyle = StyleSheet.create({
    horiz: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    viewOnly: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    wide: {
        flex: 1,
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    button: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        height: 40,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        backgroundColor: colorSurfacePrimary,
    },
    hover: {
        backgroundColor: translucentHoverColor(),
    },
    pressed: {
        borderColor: colorBorderSelected,
        backgroundColor: translucentPressColor(),
    },
});

export type FilterButtonProps = { emoji?: string; label?: TLabel; text?: string; count?: number; selected?: boolean; onPress?: () => void };

export function FilterButton({ emoji, label, text, count, selected = false, onPress }: FilterButtonProps) {
    const s = FilterButtonStyle;
    const [pressed, setPressed] = useState(false);

    return (
        <HoverView
            hoverStyle={s.hover}
            onPress={onPress}
            pressedStyle={s.pressed}
            setPressed={setPressed}
            style={[s.button, selected && s.selected]}
            testID={makeTestID({ label, text })}
        >
            {emoji && (
                <PadBox right={8}>
                    <UtilityText text={emoji} type='small' weight='medium' />
                </PadBox>
            )}
            <UtilityText label={label} text={text} type='small' weight='medium' />
            {count ? <Pad size={8} /> : null}
            <UtilityText color={colorTextWarning} text={count ? count.toString() : ''} type='small' weight='medium' />
        </HoverView>
    );
}
const FilterButtonStyle = StyleSheet.create({
    button: {
        height: 32,
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 100,
        paddingHorizontal: 12,
        backgroundColor: colorSurfacePrimary,
    },
    hover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
    },
    pressed: {
        borderColor: colorBorderPrimary,
        backgroundColor: toPressColor(colorSurfacePrimary),
    },
    selected: {
        borderColor: colorBorderSelected,
    },
});

export type BannerIconButtonProps = { type: string; testID?: string; onPress: () => void };

export function BannerIconButton({ type, testID, onPress }: BannerIconButtonProps) {
    const s = BannerIconButtonStyle;
    const icon = type == 'close' ? Close : Information;
    return (
        <HoverView hoverStyle={s.hover} onPress={onPress} pressedStyle={s.pressed} style={s.regular} testID={testID}>
            {React.createElement(icon, { size: 24, color: colorTextPrimary })}
        </HoverView>
    );
}

const BannerIconButtonStyle = StyleSheet.create({
    regular: {
        width: 24,
        height: 24,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    hover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(colorSurfacePrimary),
    },
});

export type BreadCrumbProps = {
    icon?: React.ComponentType<any>;
    ariaLabel?: TLabel;
    testID?: string;
    iconProps?: Record<string, unknown>;
    onPress?: () => void;
};

export function BreadCrumb({ icon, ariaLabel, testID, iconProps = {}, onPress }: BreadCrumbProps) {
    const s = BreadCrumbStyle;
    return (
        <HoverView
            ariaLabel={ariaLabel}
            hoverStyle={s.hover}
            onPress={onPress}
            pressedStyle={s.pressed}
            role='button'
            style={s.regular}
            testID={testID}
        >
            {icon && React.createElement(icon, iconProps)}
        </HoverView>
    );
}

const BreadCrumbStyle = StyleSheet.create({
    regular: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    hover: {
        backgroundColor: toHoverColor(colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(colorSurfacePrimary),
    },
});

export type PhotoPileProps = { photoUrlList: readonly string[] };

export function PhotoPile({ photoUrlList }: PhotoPileProps) {
    const s = PhotoPileStyle;
    if (photoUrlList.length == 0) return null;
    if (photoUrlList.length == 1) {
        return <Image source={{ uri: photoUrlList[0] }} style={s.singlePhoto} />;
    } else if (photoUrlList.length == 2) {
        return (
            <View style={s.outer}>
                <Image source={{ uri: photoUrlList[0] }} style={[s.twoPhoto, { left: 0, top: 0 }]} />
                <Image source={{ uri: photoUrlList[1] }} style={[s.twoPhoto, { right: 0, bottom: 0 }]} />
            </View>
        );
    } else if (photoUrlList.length == 3) {
        return (
            <View style={s.outer}>
                <Image source={{ uri: photoUrlList[0] }} style={[s.threePhoto, { left: 0, top: 0 }]} />
                <Image source={{ uri: photoUrlList[1] }} style={[s.threePhoto, { left: 6, top: 6 }]} />
                <Image source={{ uri: photoUrlList[2] }} style={[s.threePhoto, { right: 0, bottom: 0 }]} />
            </View>
        );
    }
}
const PhotoPileStyle = StyleSheet.create({
    outer: {
        width: 32,
        height: 32,
    },
    singlePhoto: {
        width: 32,
        height: 32,
        marginRight: 4,
    },
    twoPhoto: {
        position: 'absolute',
        width: 24,
        height: 24,
        borderWidth: 2,
        borderColor: colorBorderFacepile,
    },
    threePhoto: {
        position: 'absolute',
        width: 20,
        height: 20,
        borderWidth: 2,
        borderColor: colorBorderFacepile,
    },
});
